import { NoDataComponent, CustomButton, CustomSelect, CustomInput } from '@/shared/ui/components';
import {
  ArchivedNoDataIcon,
  DatePickerSeparatorIcon,
  SortAscIcon,
  SortDescIcon,
  inforOrangeIcon,
  reloadIcon,
  searchIcon,
  calendarIcon,
} from '@/shared/ui/assets';
import { FULL_EVENT_TYPE_OPTIONS } from '@/shared/config';
import { auth, convertToMoment, GETTEXT_ALL_EVENT_TYPE, gettextEventType } from '@/shared/lib';
import { LoadingOutlined } from '@ant-design/icons';
import { DatePicker, Spin } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { BottomScrollListener } from 'react-bottom-scroll-listener';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import EventListItem from './events/eventListItem';
import { createEventListItemData } from './handler';
import { useEventsTab } from './useEventsTab';
import type { EventsTabProps } from './eventsTab.types';
import './style.scss';

const { RangePicker } = DatePicker;

dayjs.extend(utc);

/**
 * The events tab of study management, listing a study's events with date-range, event-type, sort-order and event-ID filters plus infinite scrolling. Returns null when inactive and an archived notice for archived studies, derives the picker's allowed range from the study start and stop converted into the study timezone and offset, and renders skeleton rows, a loading-more spinner or the no-data component according to load state.
 *
 * @param isActive - Whether this tab is the selected one.
 * @param data - Study details supplying start, stop, timezone, offset and archived state.
 * @param toggleECGViewer - Callback opening the ECG viewer for a chosen event.
 * @param hasLoginBanner - Whether the login banner offsets the tab layout.
 * @returns The events tab markup, or null while inactive.
 */
const EventsTab = ({ isActive, data, toggleECGViewer, hasLoginBanner }: EventsTabProps) => {
  const {
    state,
    rangePickerRef,
    handleClickViewECG,
    useBottomScrollListener,
    onClickSortOrder,
    onChangeDropdown,
    onSearchEventId,
    handleDatesChange,
    onClickReset,
  } = useEventsTab({ data, toggleECGViewer });

  if (!isActive) {
    return null;
  }

  const { start, stop, timezone, isArchived, studyTimeOffset } = data || {};
  const {
    isLoadedEventData,
    isDisabledSearchInput,
    skeletonEventData,
    eventData,
    dataFilter,
    isLoadingMore,
  } = state;

  const defaultStartDate = convertToMoment({
    isoDate: start,
    timezone,
    studyTimeOffset,
  });
  const defaultStopDate = convertToMoment({
    isoDate: stop,
    timezone,
    studyTimeOffset,
  });
  const startDate =
    dataFilter?.startSearchDate ||
    convertToMoment({
      isoDate: start,
      timezone,
      studyTimeOffset,
    });
  const stopDate =
    dataFilter?.stopSearchDate ||
    convertToMoment({
      isoDate: stop,
      timezone,
      studyTimeOffset,
    });

  return isArchived ? (
    <div
      className={classNames('events-tab animated fadeIn', {
        'has-login-banner': hasLoginBanner,
        'has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner(),
      })}
    >
      <div className="archived-study-container">
        <img src={ArchivedNoDataIcon} alt="No data icon" />
        <div className="message">
          {t`The events in this study have been archived. If you need support to view the events of this study, please contact the Biotricity customer support team.`}
        </div>
      </div>
    </div>
  ) : (
    <div
      className={classNames('events-tab animated fadeIn', {
        'has-login-banner': hasLoginBanner,
        'has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner(),
      })}
    >
      <div className="header">
        <div className="left-header">
          <RangePicker
            id="event-date-range-picker"
            name="eventDateRange"
            aria-label={t`Filter by date range`}
            ref={rangePickerRef}
            disabled={!isLoadedEventData || !!dataFilter.eventFid}
            value={[startDate, stopDate]}
            disabledDate={(current) => {
              const isDisabled =
                (defaultStartDate
                  ? current.endOf('day').isBefore(defaultStartDate.startOf('day'))
                  : false) ||
                (defaultStopDate
                  ? current.startOf('day').isAfter(defaultStopDate.endOf('day'))
                  : false);
              return isDisabled;
            }}
            allowClear={false}
            onChange={handleDatesChange}
            size="small"
            format="MM/DD/YYYY"
            suffixIcon={<img src={calendarIcon} alt="Calendar icon" />}
            separator={<img src={DatePickerSeparatorIcon} alt="Separator icon" />}
            getPopupContainer={(ele) => ele}
            inputReadOnly
          />
          <CustomSelect
            id="select-event-type"
            aria-label={t`Event type`}
            style={{ width: 140 }}
            disabled={!isLoadedEventData || !!dataFilter.eventFid}
            options={[GETTEXT_ALL_EVENT_TYPE, ...FULL_EVENT_TYPE_OPTIONS, 'Manual'].map((item) => ({
              label: gettextEventType(item),
              value: item,
            }))}
            showSearch
            allowClear={false}
            value={dataFilter.eventType || GETTEXT_ALL_EVENT_TYPE}
            name="eventType"
            onChange={onChangeDropdown}
            size="middle"
            dropdownAlign={{
              overflow: {
                adjustX: false,
                adjustY: false,
              },
            }}
          />
          <CustomButton
            id="sort-btn"
            className={classNames(dataFilter.sortOrder === 'desc' ? 'sort-desc' : 'sort-asc')}
            size="small"
            icon={<ReactSVG src={dataFilter.sortOrder === 'desc' ? SortDescIcon : SortAscIcon} />}
            disabled={!isLoadedEventData || !!dataFilter.eventFid}
            onClick={onClickSortOrder}
            type="outline"
            style="neutral"
            tooltipTitle={
              dataFilter.sortOrder === 'desc'
                ? 'Newest first, click to change'
                : 'Oldest first, click to change'
            }
            tooltipPlacement="bottom"
            ariaLabel={
              dataFilter.sortOrder === 'desc'
                ? t`Newest first, click to change`
                : t`Oldest first, click to change`
            }
          />
        </div>
        <div className="right-header">
          <CustomInput
            id="search-event-id-input"
            name="eventFid"
            aria-label={`${t`Search by`} ${t`Event ID`}`}
            size="middle"
            suffix={<img src={searchIcon} alt="searchIcon" />}
            type="number"
            placeholder={`${t`Search by`} ${t`Event ID`} `}
            disabled={!isLoadedEventData && isDisabledSearchInput}
            onChange={onSearchEventId}
            value={dataFilter.eventFid}
            allowClear
          />
          <CustomButton
            id="refresh-btn"
            size="small"
            type="outline"
            style="default"
            icon={<ReactSVG src={reloadIcon} />}
            disabled={!isLoadedEventData}
            onClick={onClickReset}
          >
            {t`Refresh`}
          </CustomButton>
        </div>
      </div>
      <div className="warning-message">
        <img src={inforOrangeIcon} alt="inforOrangeIcon" />
        {t`The following event(s) may or may not have been finalized and their classification may change upon final review`}
      </div>
      {!!eventData?.length || (!isLoadedEventData && !eventData?.length) ? (
        <div className="event-list-content">
          <div className="event-list-item-container">
            {(isLoadedEventData ? eventData : skeletonEventData).map((event: any) => (
              <EventListItem
                key={event.id}
                isEventAdvance
                isLoadedData={isLoadedEventData}
                data={createEventListItemData({
                  data,
                  event,
                  handleClickViewECG,
                })}
                event={event}
              />
            ))}

            <BottomScrollListener offset={100} onBottom={useBottomScrollListener} />

            <div className="loading-more">
              {isLoadingMore && (
                <>
                  <span role="status" aria-label={t`Loading`}>
                    <Spin
                      indicator={
                        <LoadingOutlined
                          spin
                          style={{
                            fontSize: 24,
                            color: '#2265c6',
                          }}
                        />
                      }
                    />
                  </span>
                  <div className="loading-more__text">{t`Loading more...`}</div>
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="no-data-wrapper">
          <div className="no-data">
            <NoDataComponent
              isSearching={
                !!dataFilter.eventFid ||
                !!dataFilter.eventType ||
                (!!dataFilter.startSearchDate && !!dataFilter.stopSearchDate)
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsTab;
